/**
 * Verified DGStats ingestion lineage (battery_and_pv.py → Supabase tables).
 * Interconnected Project Sites CSVs; System Size DC; Total System Cost only.
 */

export const DGSTATS_SOLAR_UPSTREAM_PRODUCT =
  'Interconnected Project Sites Data Set';

export const DGSTATS_SOLAR_INPUT_FILE_PATTERN =
  '{PGE|SCE|SDGE}_Interconnected_Project_Sites_*.csv';

export const DGSTATS_SOLAR_RAW_CAPACITY_FIELD = 'System Size DC';
export const DGSTATS_SOLAR_CAPACITY_BASIS: 'DC' = 'DC';

export const DGSTATS_SOLAR_RAW_COST_FIELD = 'Total System Cost';
export const DGSTATS_SOLAR_RAW_DATE_FIELD = 'App Approved Date';

/** Project Sites rows are current interconnected sites grouped by approval year — not historical annual additions. */
export const SOLAR_CAPACITY_TIME_SEMANTICS = 'APPROVAL_COHORT_CURRENT_SITES';

export type SolarViewGovernanceStatus =
  | 'READY'
  | 'READY_WITH_LIMITATION'
  | 'HOLD_SOURCE'
  | 'HOLD_SCIENCE';

export const SOLAR_VIEW_GOVERNANCE: Record<
  string,
  {
    status: SolarViewGovernanceStatus;
    sourceProduct: string;
    capacityBasis: 'DC';
    costBasis: string;
    timeSemantics: string;
  }
> = {
  capacity: {
    status: 'READY',
    sourceProduct: DGSTATS_SOLAR_UPSTREAM_PRODUCT,
    capacityBasis: 'DC',
    costBasis: 'n/a',
    timeSemantics:
      'Current interconnected project-site DC capacity summed by App Approved Date approval cohort.',
  },
  cumulative: {
    status: 'READY',
    sourceProduct: DGSTATS_SOLAR_UPSTREAM_PRODUCT,
    capacityBasis: 'DC',
    costBasis: 'n/a',
    timeSemantics:
      'Cumulative ordering of current-site DC capacity by approval cohort year — not historical installed capacity at each date.',
  },
  perCapita: {
    status: 'READY',
    sourceProduct: DGSTATS_SOLAR_UPSTREAM_PRODUCT,
    capacityBasis: 'DC',
    costBasis: 'n/a',
    timeSemantics:
      'Total represented Project Sites DC capacity divided by latest compatible population (current snapshot).',
  },
  cost: {
    status: 'HOLD_SOURCE',
    sourceProduct: DGSTATS_SOLAR_UPSTREAM_PRODUCT,
    capacityBasis: 'DC',
    costBasis: 'Total System Cost only (positive); ITC Cost Basis not ingested',
    timeSemantics:
      'Held: unresolved Project Sites Total System Cost vs consolidated DC capacity for multi-phase sites.',
  },
  batteryCost: {
    status: 'HOLD_SOURCE',
    sourceProduct: DGSTATS_SOLAR_UPSTREAM_PRODUCT,
    capacityBasis: 'DC',
    costBasis: 'Mean Total System Cost / System Size DC (ETL may include zero-cost rows)',
    timeSemantics:
      'Held with Cost: same TSC/DC source; tech_type_cost does not filter positive Total System Cost.',
  },
  installers: {
    status: 'READY',
    sourceProduct: DGSTATS_SOLAR_UPSTREAM_PRODUCT,
    capacityBasis: 'DC',
    costBasis: 'n/a',
    timeSemantics: 'Top-10 installer–manufacturer pairs by DC kW from Project Sites rollup.',
  },
  installersCost: {
    status: 'HOLD_SOURCE',
    sourceProduct: DGSTATS_SOLAR_UPSTREAM_PRODUCT,
    capacityBasis: 'DC',
    costBasis: 'Total System Cost / consolidated DC kW per pair',
    timeSemantics:
      'Held with Cost — Project Sites cost/capacity scope compatibility not verified.',
  },
};

export function isSolarCostViewHeld(): boolean {
  return SOLAR_VIEW_GOVERNANCE.cost.status === 'HOLD_SOURCE';
}

export function isSolarBatteryCostViewHeld(): boolean {
  return SOLAR_VIEW_GOVERNANCE.batteryCost.status === 'HOLD_SOURCE';
}

export function isSolarInstallerCostViewHeld(): boolean {
  return SOLAR_VIEW_GOVERNANCE.installersCost.status === 'HOLD_SOURCE';
}

export function isSolarCostDerivedViewHeld(view: string): boolean {
  return (
    (view === 'cost' && isSolarCostViewHeld()) ||
    (view === 'batteryCost' && isSolarBatteryCostViewHeld()) ||
    (view === 'installersCost' && isSolarInstallerCostViewHeld())
  );
}

export const SOLAR_COST_HOLD_PUBLIC_MESSAGE =
  'Reported cost metrics are temporarily unavailable while Project Sites Total System Cost compatibility with consolidated DC capacity is under review. The ETL uses Total System Cost only; ITC Cost Basis is not imported.';

export const SOLAR_BATTERY_COST_HOLD_MESSAGE =
  'Solar + storage cost comparison is temporarily unavailable for the same Project Sites cost-basis review as the primary Cost view.';

export const SOLAR_INSTALLER_COST_HOLD_MESSAGE =
  'Installer cost comparison is temporarily unavailable for the same Project Sites cost-basis review as the primary Cost view.';
